// Vendor libraries
import { createSelector } from '@ngrx/store';
import cloneDeep from 'lodash.clonedeep';
import { CustomPDFReportModalEventName } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/enums/custom-pdf-report-modal-event-name.enum';
import { CustomPDFReportType } from 'CustomPDFReportsApp/ts/enums/custom-pdf-report-type.enum';
import { SavedScatterPlotHoldingsChoice } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/enums/custom-pdf-report-saved-asset-securities-choice.enum';
// Constants / utils
import { getSecurityTypeFromId } from 'NewAngular/utils/security-utils';
import { MODEL_PORTFOLIO } from 'NewAngular/constants/core.constants';
import { REPORT_TYPE_LEGACY_TEARSHEETS } from 'CustomPDFReportsApp/ts/features/custom-pdf-report-modal/constants';
// Fetch Request
export const getReportMenuFetchRequest = (state) => state.reportMenuFetchRequest;
export const getMenuLoadingStatus = createSelector(getReportMenuFetchRequest, (fetchRequest) => fetchRequest.fetching);
// Report State
const getReportState = (state) => state;
export const getSelectedReport = createSelector(getReportState, (reportState) => reportState.selectedReport);
export const getNamingConvention = createSelector(getReportState, (reportState) => reportState.namingConvention);
export const getPrimarySecurity = createSelector(getReportState, (reportState) => reportState.primarySecurity);
export const getSecondarySecurity = createSelector(getReportState, (reportState) => reportState.secondarySecurity);
export const getAllSecurities = createSelector(getReportState, (reportState) => {
    if (reportState.secondarySecurity.securityId !== null) {
        return [reportState.primarySecurity, reportState.secondarySecurity, ...reportState.comparisonSecurities];
    }
    return [reportState.primarySecurity, ...reportState.comparisonSecurities];
});
export const getComparisonSecurities = createSelector(getReportState, (reportState) => reportState.comparisonSecurities);
export const getTalkingPoints = createSelector(getReportState, (reportState) => reportState.talkingPoints);
export const getTransitionAnalyses = createSelector(getReportState, (reportState) => reportState.transitionAnalyses);
export const getCurrentWizardStepIndex = createSelector(getReportState, (reportState) => reportState.currentWizardStepIndex);
export const getWizardSteps = createSelector(getSelectedReport, (report) => report.wizard_steps);
export const getCurrentWizardStep = createSelector(getPrimarySecurity, getSecondarySecurity, getComparisonSecurities, getWizardSteps, getCurrentWizardStepIndex, (primarySecurity, secondarySecurity, comparisonSecurities, wizardSteps, stepIndex) => {
    if (wizardSteps === undefined || stepIndex === null || stepIndex > (wizardSteps ?? []).length - 1) {
        return null;
    }
    const step = cloneDeep(wizardSteps[stepIndex]);
    // Enable Model Portfolio-specific holdings options for scatter plots on the report if applicable
    if (step.eventName === CustomPDFReportModalEventName.SHOW_SECURITY_SEARCH_MODAL &&
        'scatter_plot_securities_choice' in (step.extraFields ?? {})) {
        let choicesToEnable = new Set();
        if (getSecurityTypeFromId(primarySecurity.securityId ?? '') === MODEL_PORTFOLIO) {
            choicesToEnable = new Set(Object.values(SavedScatterPlotHoldingsChoice));
        }
        else if ([secondarySecurity, ...comparisonSecurities].some((security) => getSecurityTypeFromId(security.securityId ?? '') === MODEL_PORTFOLIO)) {
            choicesToEnable = new Set([
                SavedScatterPlotHoldingsChoice.ADD_REPORT_SECURITIES_HOLDINGS,
                SavedScatterPlotHoldingsChoice.REPORT_SECURITIES_HOLDINGS_ONLY,
            ]);
        }
        step.extraFields.scatter_plot_securities_choice.choices =
            step.extraFields.scatter_plot_securities_choice.choices.map((choice) => {
                return { ...choice, disabled: choice.disabled && !choicesToEnable.has(choice.value) };
            });
    }
    return step;
});
export const getWizardStepEventName = createSelector(getCurrentWizardStepIndex, getWizardSteps, (currentWizardStepIndex, wizardSteps) => {
    if (wizardSteps === undefined || currentWizardStepIndex === null)
        return null;
    // if the index is equal to the length, this means we have finished all the wizard steps
    // and now need to generate the report
    if (currentWizardStepIndex === wizardSteps.length) {
        return CustomPDFReportModalEventName.GENERATE_CUSTOM_PDF_REPORT;
    }
    return wizardSteps[currentWizardStepIndex].eventName;
});
export const getIsLastStep = createSelector(getCurrentWizardStepIndex, getWizardSteps, (currentWizardStepIndex, wizardSteps) => {
    if (wizardSteps === undefined || currentWizardStepIndex === null)
        return null;
    return currentWizardStepIndex >= wizardSteps.length - 1;
});
export const getDefaultReportTitle = createSelector(getSelectedReport, getPrimarySecurity, getSecondarySecurity, (report, primarySecurity, secondarySecurity) => {
    const { report_name, report_type } = report;
    switch (report_type) {
        case CustomPDFReportType.AGGREGATE_SECURITY_COMPARISON:
            return `${primarySecurity.securityName} vs. ${secondarySecurity.securityName} ${report_name}`;
        case CustomPDFReportType.AGGREGATE_SECURITY_OVERVIEW:
            return `${primarySecurity.securityName} ${report_name}`;
        case CustomPDFReportType.MULTI_SECURITY_COMPARISON:
            return `${primarySecurity.securityName} vs. Others ${report_name}`;
        case CustomPDFReportType.PRESENTATION:
            return `Presentation ${report_name}`;
        case CustomPDFReportType.TEARSHEET_SINGLE_SECURITY:
            return `${primarySecurity.securityName} ${report_name}`;
        case CustomPDFReportType.TEARSHEET_MULTI_SECURITY_COMPARISON:
            return `${primarySecurity.securityName} vs. Others ${report_name}`;
        default:
            return report_name;
    }
});
export const getCoverPageInfo = createSelector(getReportState, (reportState) => reportState.coverPageInfo);
export const getUserPreferenceFetchRequest = createSelector(getReportState, (reportState) => reportState.userPreferenceFetchRequest);
export const getCoverPageLoadingStatus = createSelector(getUserPreferenceFetchRequest, (fetchRequest) => fetchRequest.fetching);
export const getCoverPageErrorStatus = createSelector(getUserPreferenceFetchRequest, (fetchRequest) => fetchRequest.error);
export const getCustomPdfReportMenu = createSelector(getReportState, (reportState) => {
    return reportState.customPdfReportMenu;
});
export const getCurrentReportTypeIndex = createSelector(getReportState, (reportState) => reportState.currentReportTypeIndex);
const getCurrentReportType = createSelector(getReportState, (reportState) => {
    const reportMenu = reportState.customPdfReportMenu;
    if (!!reportMenu && reportMenu.length > 0) {
        return reportMenu[reportState.currentReportTypeIndex];
    }
    return null;
});
export const getCurrentReportTypeName = createSelector(getCurrentReportType, (reportType) => {
    return reportType !== null ? reportType.name : '';
});
export const getReportsForReportType = createSelector(getCurrentReportType, (reportType) => {
    if (reportType !== null) {
        return reportType.reports;
    }
    return [];
});
export const getCurrentReportIndex = createSelector(getReportState, (reportState) => reportState.currentReportIndex);
export const getGeneratingReport = createSelector(getReportState, (reportState) => reportState.generatingSavedReport);
export const getGeneratingUnsavedReport = createSelector(getReportState, (reportState) => reportState.generateReportConfig);
export const getGenerationErrors = createSelector(getReportState, (reportState) => reportState.generationErrors);
export const getTearsheetsReportUrl = createSelector(getReportState, (reportState) => reportState.selectedReport.tearsheets_report_url || null);
export const getYRatingReportUrl = createSelector(getReportState, (reportState) => reportState.selectedReport.y_rating_report_url || null);
export const getReportTitle = createSelector(getReportState, (reportState) => reportState.reportTitle);
export const getSavedAssetSecuritiesSelections = createSelector(getReportState, (reportState) => {
    return {
        fund_chart_securities_choice: reportState.fundChartSecuritiesChoice,
        scatter_plot_securities_choice: reportState.scatterPlotSecuritiesChoice,
        comp_table_securities_choice: reportState.compTableSecuritiesChoice,
        timeseries_table_securities_choice: reportState.timeseriesTableSecuritiesChoice,
    };
});
export const getSavedHypotheticalId = createSelector(getReportState, (reportState) => reportState.savedHypotheticalId);
export const getSavedAnalysisPlanId = createSelector(getReportState, (reportState) => reportState.savedAnalysisPlanId);
export const getRiskProfile = createSelector(getReportState, (reportState) => reportState.riskProfile);
export const getSecurityLevelValues = createSelector(getReportState, (reportState) => reportState.securityLevelValues);
export const generatePostParams = createSelector(getSelectedReport, getPrimarySecurity, getSecondarySecurity, getComparisonSecurities, getReportTitle, getCoverPageInfo, getSavedAssetSecuritiesSelections, getSavedHypotheticalId, getSavedAnalysisPlanId, getRiskProfile, getTalkingPoints, getTransitionAnalyses, getNamingConvention, getSecurityLevelValues, (report, primarySecurity, secondarySecurity, comparisonSecurities, reportTitle, coverPageInfo, savedAssetSecuritiesSelections, savedHypotheticalId, savedAnalysisPlanId, riskProfile, talkingPoints, transitionAnalyses, namingConvention, securityLevelValues) => {
    const { report_type } = report;
    switch (report_type) {
        case CustomPDFReportType.AGGREGATE_SECURITY_COMPARISON:
            return {
                primary_security_id: primarySecurity.securityId,
                secondary_security_id: secondarySecurity.securityId,
                contact_email: coverPageInfo.contactEmail,
                contact_phone: coverPageInfo.contactPhone,
                contact_url: coverPageInfo.contactUrl,
                organization: coverPageInfo.organization,
                prepared_by: coverPageInfo.preparedBy,
                prepared_for: coverPageInfo.preparedFor,
                report_title: reportTitle,
                as_of_date_option: coverPageInfo.asOfDateOption,
                designation_type: coverPageInfo.designationType,
                saved_hypothetical_id: savedHypotheticalId,
                saved_analysis_plan_id: savedAnalysisPlanId,
                risk_profile: riskProfile,
                talking_points: talkingPoints,
                serialized_transition_analyses: transitionAnalyses,
                naming_convention: namingConvention,
                dollar_conversion_levels: securityLevelValues,
                custom_security_labels: [primarySecurity.customLabel, secondarySecurity.customLabel],
                securitylist_group: primarySecurity.securityListGroup,
                ...savedAssetSecuritiesSelections,
            };
        case CustomPDFReportType.AGGREGATE_SECURITY_OVERVIEW:
            return {
                security_id: primarySecurity.securityId,
                contact_email: coverPageInfo.contactEmail,
                contact_phone: coverPageInfo.contactPhone,
                contact_url: coverPageInfo.contactUrl,
                organization: coverPageInfo.organization,
                prepared_by: coverPageInfo.preparedBy,
                prepared_for: coverPageInfo.preparedFor,
                report_title: reportTitle,
                as_of_date_option: coverPageInfo.asOfDateOption,
                designation_type: coverPageInfo.designationType,
                saved_hypothetical_id: savedHypotheticalId,
                saved_analysis_plan_id: savedAnalysisPlanId,
                risk_profile: riskProfile,
                talking_points: talkingPoints,
                serialized_transition_analyses: transitionAnalyses,
                naming_convention: namingConvention,
                dollar_conversion_levels: securityLevelValues,
                custom_security_labels: [primarySecurity.customLabel],
                securitylist_group: primarySecurity.securityListGroup,
                ...savedAssetSecuritiesSelections,
            };
        case CustomPDFReportType.MULTI_SECURITY_COMPARISON:
            const securityIds = [
                primarySecurity.securityId,
                ...comparisonSecurities.map((security) => security.securityId),
            ];
            const customLabels = [
                primarySecurity.customLabel,
                ...comparisonSecurities.map((security) => security.customLabel),
            ];
            return {
                security_ids: securityIds,
                contact_email: coverPageInfo.contactEmail,
                contact_phone: coverPageInfo.contactPhone,
                contact_url: coverPageInfo.contactUrl,
                organization: coverPageInfo.organization,
                prepared_by: coverPageInfo.preparedBy,
                prepared_for: coverPageInfo.preparedFor,
                report_title: reportTitle,
                as_of_date_option: coverPageInfo.asOfDateOption,
                designation_type: coverPageInfo.designationType,
                naming_convention: namingConvention,
                dollar_conversion_levels: securityLevelValues,
                custom_security_labels: customLabels,
                securitylist_group: primarySecurity.securityListGroup,
                ...savedAssetSecuritiesSelections,
            };
        case CustomPDFReportType.PRESENTATION:
            return {
                contact_email: coverPageInfo.contactEmail,
                contact_phone: coverPageInfo.contactPhone,
                contact_url: coverPageInfo.contactUrl,
                organization: coverPageInfo.organization,
                prepared_by: coverPageInfo.preparedBy,
                prepared_for: coverPageInfo.preparedFor,
                report_title: reportTitle,
                dollar_conversion_levels: securityLevelValues,
                as_of_date_option: coverPageInfo.asOfDateOption,
                designation_type: coverPageInfo.designationType,
                ...savedAssetSecuritiesSelections,
            };
        case CustomPDFReportType.TEARSHEET_SINGLE_SECURITY:
            return {
                security_id: primarySecurity.securityId,
                contact_email: coverPageInfo.contactEmail,
                contact_phone: coverPageInfo.contactPhone,
                contact_url: coverPageInfo.contactUrl,
                organization: coverPageInfo.organization,
                prepared_by: coverPageInfo.preparedBy,
                prepared_for: coverPageInfo.preparedFor,
                report_title: reportTitle,
                as_of_date_option: coverPageInfo.asOfDateOption,
                designation_type: coverPageInfo.designationType,
                saved_hypothetical_id: savedHypotheticalId,
                saved_analysis_plan_id: savedAnalysisPlanId,
                risk_profile: riskProfile,
                talking_points: talkingPoints,
                naming_convention: namingConvention,
                dollar_conversion_levels: securityLevelValues,
                custom_security_labels: [primarySecurity.customLabel],
                securitylist_group: primarySecurity.securityListGroup,
                ...savedAssetSecuritiesSelections,
            };
        case CustomPDFReportType.TEARSHEET_MULTI_SECURITY_COMPARISON:
            const tearsheetSecurityIds = [
                primarySecurity.securityId,
                ...comparisonSecurities.map((security) => security.securityId),
            ];
            const tearsheetCustomLabels = [
                primarySecurity.customLabel,
                ...comparisonSecurities.map((security) => security.customLabel),
            ];
            return {
                security_ids: tearsheetSecurityIds,
                contact_email: coverPageInfo.contactEmail,
                contact_phone: coverPageInfo.contactPhone,
                contact_url: coverPageInfo.contactUrl,
                organization: coverPageInfo.organization,
                prepared_by: coverPageInfo.preparedBy,
                prepared_for: coverPageInfo.preparedFor,
                report_title: reportTitle,
                as_of_date_option: coverPageInfo.asOfDateOption,
                designation_type: coverPageInfo.designationType,
                naming_convention: namingConvention,
                dollar_conversion_levels: securityLevelValues,
                custom_security_labels: tearsheetCustomLabels,
                securitylist_group: primarySecurity.securityListGroup,
                ...savedAssetSecuritiesSelections,
            };
        case REPORT_TYPE_LEGACY_TEARSHEETS:
            return { as_of_date_option: coverPageInfo.asOfDateOption };
        default:
            return {};
    }
});
